import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, CreditCard, Download, Globe, Palette, Plug, ShieldCheck, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { applyBranding } from "@/lib/branding";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Spinner } from "@/components/States";
import { BrandPreview, ColorField, ImageField, SurfaceField } from "@/components/workspace/BrandingFields";
import QuickBooksCard from "@/components/workspace/QuickBooksCard";
import { ClientSettings, TabOrder } from "@/components/workspace/WorkspaceSettings";
import { DataExport, WebsiteKit } from "@/components/workspace/WebsiteKit";
import { AiUsageCard, AuditLog, MfaCard, PoliciesCard, RequireMfaCard } from "@/components/workspace/SecurityCards";
import Billing from "@/pages/Billing";
import PlanningTemplates from "@/components/workspace/PlanningTemplates";
import AccountCard from "@/components/workspace/AccountCard";

const pick = (ws) => ({ name: ws.name, slug: ws.slug, ...ws.branding, tab_order: ws.settings?.tab_order, nudges_enabled: ws.settings?.nudges_enabled !== false, digest_enabled: ws.settings?.digest_enabled !== false });
const SECTIONS = [
  ["branding", "Branding", Palette], ["client", "Client portal", Users], ["planning", "Planning", ClipboardList], ["website", "Website", Globe],
  ["integrations", "Integrations", Plug], ["billing", "Billing", CreditCard], ["security", "Security", ShieldCheck], ["export", "Export", Download],
];

export default function Workspace() {
  const { workspace, setWorkspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(() => pick(workspace));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const pro = workspace.state.limits.white_label;
  const owner = workspace.kind === "owner_builder";
  const hidden = workspace.hidden || [];
  const sections = SECTIONS.filter(([k]) => owner ? ["branding", "security", "export"].includes(k) : true).filter(([k]) => !(k === "billing" && hidden.includes("billing")));
  const section = sections.some(([k]) => k === params.get("section")) ? params.get("section") : "branding";
  const go = (k) => setParams(k === "branding" ? {} : { section: k });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- applyBranding is a stable module import
  useEffect(() => { applyBranding(form); }, [form]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restore branding on unmount only
  useEffect(() => () => applyBranding(workspace.branding), [workspace]);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.put("/workspace", form); setWorkspace(data); toast.success("Settings saved"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const SaveBar = ({ label = "Save" }) => <div className="flex justify-end"><Button type="submit" className="rounded-full" disabled={busy} data-testid="ws-save-button">{busy && <Spinner />} {label}</Button></div>;

  return (
    <div className="max-w-6xl space-y-6 p-4 md:p-8">
      <PageHeader eyebrow="Workspace" title={owner ? "Settings" : "Settings & Branding"} />
      {params.get("welcome") && (
        <div className="flex items-start gap-3 rounded-xl border bg-secondary p-5 text-sm" data-testid="welcome-card">
          <Sparkles className="mt-0.5 h-5 w-5 text-accent" />
          <div><p className="font-semibold">Welcome to your workspace.</p><p className="text-muted-foreground">Make it yours: add your logo and colours, then head to Projects to start your first job.</p></div>
        </div>
      )}
      <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <nav className="flex w-max min-w-full gap-1 border-b" data-testid="settings-sections">
          {sections.map(([k, l, Icon]) => <button key={k} type="button" onClick={() => go(k)} aria-current={section === k} className={`tab-trigger inline-flex items-center gap-1.5 ${section === k ? "border-primary text-foreground" : ""}`} data-testid={`settings-tab-${k}`}><Icon className="h-3.5 w-3.5" />{l}</button>)}
        </nav>
      </div>

      {section === "branding" && (
        <form onSubmit={save} className="grid gap-8 lg:grid-cols-[1fr_340px]" data-testid="branding-form">
          <div className="space-y-6">
            <section className="grid gap-5 rounded-xl border bg-card p-5 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2"><Label htmlFor="ws-name">{owner ? "Project / household name" : "Company name"}</Label><Input id="ws-name" required minLength={2} value={form.name} onChange={set("name")} data-testid="ws-name-input" /></div>
              <div className="space-y-2 sm:col-span-2"><Label htmlFor="ws-tagline">Tagline</Label><Input id="ws-tagline" maxLength={140} placeholder="Cottage country design and renovation" value={form.tagline || ""} onChange={set("tagline")} data-testid="ws-tagline-input" /></div>
              <ColorField id="ws-primary" label="Primary colour" value={form.primary_color} onChange={set("primary_color")} />
              <ColorField id="ws-accent" label="Accent colour" value={form.accent_color} onChange={set("accent_color")} />
              <SurfaceField value={form.surface || "grey"} onChange={set("surface")} />
            </section>
            <section className="grid gap-6 rounded-xl border bg-card p-5 sm:grid-cols-2">
              <ImageField id="logo" label="Logo" hint="PNG or SVG with a transparent background works best." value={form.logo_url} onChange={set("logo_url")} />
              <ImageField id="hero" label="Login image" hint="Shown beside the sign-in form for your team and clients." value={form.hero_image_url} onChange={set("hero_image_url")} />
            </section>
            <section className="space-y-5 rounded-xl border bg-card p-5">
              <div className="space-y-2">
                <Label htmlFor="ws-slug" className="flex items-center gap-2"><Globe className="h-4 w-4" />Subdomain {!pro && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider">Pro</span>}</Label>
                <div className="flex items-center gap-2"><Input id="ws-slug" value={form.slug} onChange={set("slug")} disabled={!pro} className="font-mono" data-testid="ws-slug-input" /><span className="whitespace-nowrap text-sm text-muted-foreground">.elm.build</span></div>
                <p className="text-xs text-muted-foreground">Your team's sign-in link: <code className="break-all font-mono" data-testid="ws-login-link">{window.location.origin}/login?ws={form.slug}</code></p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ws-sender">Email sender name</Label>
                <Input id="ws-sender" placeholder={form.name} value={form.email_sender_name || ""} onChange={set("email_sender_name")} data-testid="ws-sender-input" />
                <p className="text-xs text-muted-foreground">Invites and reminders come from this name.</p>
              </div>
            </section>
            <TabOrder value={form.tab_order || ["schedule", "board", "prefs", "wishlist", "photos", "estimate", "expenses", "site", "settings"]} onChange={set("tab_order")} />
            <SaveBar label={owner ? "Save settings" : "Save branding"} />
          </div>
          <aside className="space-y-3 lg:sticky lg:top-6 lg:self-start">
            <p className="eyebrow">Live preview</p>
            <BrandPreview form={form} />
            <p className="text-xs text-muted-foreground">The sidebar and buttons update as you type. Nothing is saved until you press Save.</p>
          </aside>
        </form>
      )}
      {section === "client" && (
        <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="client-settings-form">
          <ClientSettings nudges={form.nudges_enabled} onNudges={set("nudges_enabled")} digest={form.digest_enabled} onDigest={set("digest_enabled")} slug={workspace.slug} />
          <SaveBar />
        </form>
      )}
      {section === "planning" && <div className="max-w-3xl" data-testid="planning-section"><PlanningTemplates /></div>}
      {section === "website" && <div className="max-w-3xl" data-testid="website-section"><WebsiteKit slug={workspace.slug} primary={form.primary_color} /></div>}
      {section === "integrations" && <div className="max-w-3xl" data-testid="integrations-section"><QuickBooksCard /></div>}
      {section === "billing" && <div data-testid="billing-section"><Billing embedded /></div>}
      {section === "security" && <div className="max-w-3xl space-y-6" data-testid="security-section"><AccountCard /><MfaCard /><RequireMfaCard /><AiUsageCard /><PoliciesCard /><AuditLog /></div>}
      {section === "export" && <div className="max-w-3xl" data-testid="export-section"><DataExport /></div>}
    </div>
  );
}
